import { ArrowBack } from '@mui/icons-material';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import {
  Button,
  ButtonVariants,
  ModalContentContainer,
  TextField,
} from '@/components';
import { ComplianceTypes, Routes, urls } from '@/constants';
import { Link } from '@/containers';
import { useForm } from '@/hooks';
import { CheckmarkIcon } from '@/icons';
import { AnalyticsService, EVENT_CONSTANTS, EVENTS, log } from '@/services';
import {
  selectActiveModalParams,
  selectEpisodeCompliance,
  useAppSelector,
  useFetchEpisodeQuery,
  useFetchUserIpRegionQuery,
  useGetInviteDetailsQuery,
  useLazyGetSSORedirectUriQuery,
  useRegisterMutation,
  useResendSSOConfirmationEmailMutation,
} from '@/store';
import { appStyles, theme } from '@/theme';
import { Referrer } from '@/types';
import {
  convertSecondsToMinutes,
  getClientId,
  getSignupPrefillCookie,
  getUtmParametersCookie,
  parseUtmParameters,
  removeSignupPrefillCookie,
  SnackbarUtils,
} from '@/utils';

import { OauthButton } from '../components';
import { MarketingConsentCheckbox } from './MarketingConsentCheckbox';
import { RegisterFormState } from './types';

type RegisterFormProps = {
  subtitle?: string;
  showLeftColumn?: boolean;
  variant?: (typeof FormVariants)[keyof typeof FormVariants];
  stretch?: boolean;
  skipToRegisterStep?: boolean;
  onLoginClick?: () => void;
  onRegisterSuccess?: () => void;
  onModalClose?: () => void;
  referrer?: Referrer;
};

const Steps = {
  INITIAL: 0,
  REGISTER: 1,
  SUCCESS: 2,
} as const;

const benefits = [
  'Exclusive guests',
  'Variety of topics',
  'New episodes weekly',
];

export const FormVariants = {
  FREE_TRIAL: 'freetrial',
  FREE_TRIAL_CPD: 'freetrial_cpd',
  FREE_TRIAL_CPE: 'freetrial_cpe',
} as const;

export const RegisterForm = ({
  subtitle,
  showLeftColumn = false,
  variant,
  stretch = false,
  skipToRegisterStep,
  onLoginClick,
  onRegisterSuccess,
  onModalClose,
  referrer,
}: RegisterFormProps) => {
  const location = useLocation();
  const {
    inviteToken,
    status,
    providerId,
    provider,
    email: oauthEmail,
    redirect,
  } = Object.fromEntries(new URLSearchParams(location.search).entries());

  const { episodeId, isMarketingModal } =
    useAppSelector(selectActiveModalParams) ?? {};
  const compliance = useAppSelector(state =>
    selectEpisodeCompliance(state, episodeId),
  );

  const { data: episode } = useFetchEpisodeQuery(
    {
      episodeId,
      compliance,
    },
    { skip: !episodeId },
  );

  const episodeValue =
    compliance === ComplianceTypes.CPD
      ? `${convertSecondsToMinutes(episode?.duration ?? 0)} minutes of CPD`
      : `${episode?.CPEDetails?.credits} credits of CPE`;

  const emailPrefill = useMemo(() => {
    let email = '';
    const cookie = getSignupPrefillCookie();
    try {
      if (cookie) email = JSON.parse(cookie)?.email;
      if (email) {
        AnalyticsService.logEvent(EVENTS.AUTH_EVENTS.REGISTER_EMAIL_PREFILLED, {
          prefillEmail: email,
        });
      }
    } catch (error) {
      log('Error parsing signup prefill cookie:', error);
    }
    removeSignupPrefillCookie();
    return email;
  }, []);

  const { form, setForm, errors, setErrors, validateForm, onChange } =
    useForm<RegisterFormState>({
      email: emailPrefill,
      newPassword: '',
      confirmPassword: '',
      marketingConsent: false,
    });
  const [step, setStep] = useState<(typeof Steps)[keyof typeof Steps]>(
    status === 'awaiting' ? Steps.SUCCESS : Steps.INITIAL,
  );

  const { data: country } = useFetchUserIpRegionQuery();

  const [register, { isLoading: isRegisterLoading }] = useRegisterMutation();
  const [resendSSOConfirmationEmail] = useResendSSOConfirmationEmailMutation();

  const { data: inviteDetails, isLoading: isInviteDetailsLoading } =
    useGetInviteDetailsQuery(
      {
        inviteToken: inviteToken || '',
      },
      {
        skip: !inviteToken,
      },
    );
  const [trigger, { isLoading: isSSORedirectLoading }] =
    useLazyGetSSORedirectUriQuery();

  useEffect(() => {
    if (skipToRegisterStep) {
      setStep(Steps.REGISTER);
    }
  }, [skipToRegisterStep]);

  useEffect(() => {
    if (!inviteDetails) return;
    setForm({
      email: inviteDetails.email,
      newPassword: '',
      confirmPassword: '',
      marketingConsent: form.marketingConsent,
    });
  }, [inviteDetails, setForm, form.marketingConsent]);

  useEffect(() => {
    if (country === 'USA') {
      setForm(prev => ({
        ...prev,
        marketingConsent: true,
      }));
    }
  }, [country, setForm]);

  const onContinueToRegister = async () => {
    const isValid = validateForm(['email']);
    if (!isValid) {
      return;
    }
    const res = await trigger({
      email: form.email.trim(),
      redirect,
    });
    if (res.data) {
      AnalyticsService.logEvent(EVENTS.AUTH_EVENTS.SSO_REDIRECT);
      window.location.href = res.data;
    } else {
      setStep(Steps.REGISTER);
    }
  };

  const onSubmit = async () => {
    if (providerId) {
      return resendSSOConfirmationEmail({
        email: oauthEmail,
        providerId,
        provider,
      });
    }
    const isValid = validateForm(['email', 'newPassword', 'confirmPassword']);
    if (!isValid) {
      return;
    }

    const { offerId, inviteToken } = Object.fromEntries(
      new URLSearchParams(location.search),
    );

    try {
      const utmParams = parseUtmParameters(getUtmParametersCookie() ?? '');
      const clientId = getClientId();

      const { accessToken, refreshToken } = await register({
        email: form.email.trim(),
        password: form.newPassword.trim(),
        offerId,
        inviteToken,
        marketingOptIn: form.marketingConsent,
        utmParams: {
          ...Object.fromEntries(new URLSearchParams(location.search)),
          ...utmParams,
          ...(clientId ? { client_id: clientId } : {}),
        },
        referrer,
      }).unwrap();

      if (!accessToken || !refreshToken) {
        setStep(Steps.SUCCESS);
        AnalyticsService.logEvent(EVENTS.AUTH_EVENTS.REGISTER_STEP, {
          stepName: 'check your email',
        });
      } else {
        onRegisterSuccess?.();
      }
    } catch (error) {
      const errorMessage = (error as { data: { message: string } })?.data
        .message;

      if (errorMessage) {
        setStep(Steps.INITIAL);
        setForm({
          email: form.email,
          newPassword: '',
          confirmPassword: '',
          marketingConsent: form.marketingConsent,
        });
        setErrors({
          email: errorMessage,
          newPassword: undefined,
          confirmPassword: undefined,
          marketingConsent: undefined,
        });
      } else {
        setErrors(prev => ({
          ...prev,
          newPassword: 'Something went wrong. Please try again later.',
        }));
      }
      AnalyticsService.logEvent(EVENTS.AUTH_EVENTS.REGISTER.FAIL, {
        reason: errorMessage || '',
      });
    }
  };

  const leftColumnContents = showLeftColumn ? (
    <Box
      sx={{
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        justifyContent: 'center',
        gap: 3,
      }}>
      <Typography variant="h4" color="white">
        {episode?.name}
      </Typography>
      {episodeId || !!episode ? (
        <>
          <Box
            sx={{
              position: 'relative',
              width: 170,
              height: 170,
              borderRadius: 4,
              overflow: 'hidden',
              mx: 'auto',
              boxShadow: '0px 4px 12px 0px rgba(0, 0, 0, 0.65)',
            }}>
            <Box
              component="img"
              alt="episode cover"
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'center',
              }}
              src={episode?.cover}
            />
            <Box
              component="img"
              src={episode?.companyLogo}
              alt="company logo"
              sx={{
                position: 'absolute',
                top: 8,
                left: 8,
                borderRadius: 2,
                boxShadow: '0px 4px 16px 0px rgba(0, 0, 0, 0.35)',
                height: 38,
                width: 38,
              }}
            />
          </Box>
          <Typography
            variant="body3"
            color="text.contrast"
            sx={{ display: 'inline' }}>
            Listen now to earn{' '}
            <span
              style={{
                color: theme.palette.teal200,
                fontWeight: theme.typography.body3SemiBold.fontWeight,
              }}>
              {episodeValue}
            </span>
          </Typography>
        </>
      ) : (
        <>
          <Typography variant="body2" color="white">
            Listen to over a thousand engaging podcasts, all while earning
            verified {compliance}.
          </Typography>
          <Box sx={styles.benefitList}>
            {benefits.map((benefit, index) => (
              <Box key={index} sx={styles.benefitItem}>
                <CheckmarkIcon sx={styles.checkboxIcon} />
                <Typography variant="body2" color="white">
                  {benefit}
                </Typography>
              </Box>
            ))}
          </Box>
        </>
      )}
    </Box>
  ) : null;

  return (
    <ModalContentContainer
      onModalClose={onModalClose}
      stretch={stretch}
      leftColumnWidth={300}
      leftColumnContents={leftColumnContents}>
      {isInviteDetailsLoading ? (
        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          <CircularProgress />
        </Box>
      ) : step !== Steps.SUCCESS ? (
        <>
          <Typography variant="h1" sx={{ whiteSpace: 'pre-wrap', mb: 1 }}>
            {isMarketingModal
              ? 'Sign up and listen\nfor free.'
              : 'Create an account'}
          </Typography>
          {(!!subtitle || isMarketingModal) && (
            <Typography
              variant="h4"
              sx={[
                appStyles.gradientText,
                {
                  mb: 2,
                  whiteSpace: 'pre-wrap',
                },
              ]}>
              {isMarketingModal
                ? `Your trial includes 3 ${compliance} hours, so this episode is on us. No credit card required.`
                : subtitle}
            </Typography>
          )}
          {inviteDetails && (
            <Typography variant="h4" sx={{ mt: 1 }}>
              Join your colleagues at {inviteDetails.organizationName}
            </Typography>
          )}
          {!!variant && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                backgroundColor: 'grey50',
                borderRadius: 2,
                p: 1.5,
                pl: 2,
                gap: 1.5,
                alignItems: 'center',
                mb: 3,
                mt: 1,
              }}>
              <Box
                sx={{
                  flexGrow: 1,
                  color: 'text.main',
                }}>
                <Typography variant="h4">Start your free trial!</Typography>
                <Typography variant="body3SemiBold">
                  No credit card required.
                </Typography>
              </Box>
              <Box
                sx={{
                  borderRadius: 2,
                  backgroundColor: 'white',
                  boxShadow: '0px 3px 9px 0px rgba(0, 0, 0, 0.2)',
                  px: 1.5,
                  py: { xs: 0.5, md: 0 },
                  display: 'flex',
                  gap: 1.5,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                <Typography
                  variant="hero4"
                  color="accent.main"
                  sx={appStyles.gradientText}>
                  3
                </Typography>
                <Typography variant="body3" sx={{ textWrap: 'nowrap' }}>
                  Free{' '}
                  {variant === FormVariants.FREE_TRIAL_CPE ||
                  (variant === FormVariants.FREE_TRIAL && compliance === 'CPE')
                    ? 'CPE'
                    : 'CPD'}
                  <br />
                  {variant === FormVariants.FREE_TRIAL_CPE ||
                  (variant === FormVariants.FREE_TRIAL && compliance === 'CPE')
                    ? ' Credits'
                    : ' Hours'}
                </Typography>
              </Box>
            </Box>
          )}
          <Box>
            <Typography variant="body2" sx={{ display: 'inline' }}>
              Already have an account?{' '}
            </Typography>
            <Link
              textVariant="body2"
              to={
                onLoginClick ? undefined : `${Routes.LOGIN}${location.search}`
              }
              onClick={onLoginClick}>
              Log In
            </Link>
          </Box>
          <Box>
            <TextField
              id="email"
              fullWidth
              label="Email"
              placeholder="Enter your email"
              type="email"
              sx={{ mt: 6 }}
              value={form.email}
              error={!!errors.email}
              helperText={errors.email}
              disabled={step === Steps.REGISTER && !skipToRegisterStep}
              onChange={onChange}
              onEnterPress={onContinueToRegister}
            />
          </Box>
          {step === Steps.INITIAL ? (
            <>
              <Box sx={{ mt: 2 }}>
                <MarketingConsentCheckbox
                  marketingOptIn={form.marketingConsent}
                  onChange={value => {
                    setForm(prev => ({
                      ...prev,
                      marketingConsent: value,
                    }));
                  }}
                  country={country}
                />
              </Box>
              <Button
                label="Continue with email"
                skinny={false}
                fullWidth
                sx={{ mt: 2, mb: 6 }}
                onClick={onContinueToRegister}
                disabled={isSSORedirectLoading}
              />
              <Stack direction="column" gap={1.5} mb={1}>
                <OauthButton
                  type="google"
                  marketingConsent={form.marketingConsent}
                />
                <OauthButton
                  type="apple"
                  marketingConsent={form.marketingConsent}
                />
              </Stack>
              <Typography variant="caption1Bold" sx={{ color: 'text.light' }}>
                By clicking &quot;continue&quot;, I agree to LumiQ&apos;s{' '}
                <Link
                  to={urls.lumiqTC}
                  target="_blank"
                  textVariant="caption1Bold">
                  Terms &amp; Conditions
                </Link>{' '}
                and acknowledge the{' '}
                <Link
                  to="https://lumiqlearn.com/ca/privacy-policy"
                  target="_blank"
                  textVariant="caption1Bold">
                  Privacy Policy
                </Link>
                .
              </Typography>
            </>
          ) : (
            <Stack direction="column" gap={2} mt={2}>
              <TextField
                id="newPassword"
                value={form.newPassword}
                fullWidth
                label="Create a Password"
                placeholder="Enter your password"
                type="password"
                error={!!errors.newPassword}
                helperText={errors.newPassword}
                onChange={onChange}
                onEnterPress={onSubmit}
              />
              <TextField
                id="confirmPassword"
                value={form.confirmPassword}
                fullWidth
                label="Confirm Password"
                placeholder="Re-enter your password"
                type="password"
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword}
                onChange={onChange}
                onEnterPress={onSubmit}
              />
              <MarketingConsentCheckbox
                marketingOptIn={form.marketingConsent}
                onChange={value => {
                  setForm(prev => ({
                    ...prev,
                    marketingConsent: value,
                  }));
                }}
                country={country}
              />
              <Button
                label="Agree and Create Account"
                fullWidth
                skinny={false}
                onClick={onSubmit}
                disabled={isRegisterLoading}
              />
              <Box sx={{ alignSelf: 'flex-start' }}>
                <Button
                  variant={ButtonVariants.PLAIN}
                  skinny={false}
                  label="Go back"
                  onClick={() => {
                    setStep(Steps.INITIAL);
                    setForm({
                      email: form.email,
                      newPassword: '',
                      confirmPassword: '',
                      marketingConsent: form.marketingConsent,
                    });
                    setErrors({
                      email: undefined,
                      newPassword: undefined,
                      confirmPassword: undefined,
                      marketingConsent: undefined,
                    });
                  }}
                  startIcon={<ArrowBack />}
                />
              </Box>
            </Stack>
          )}
        </>
      ) : (
        <>
          <Typography variant="h1">
            Please confirm your email address
          </Typography>
          <Typography variant="body1" sx={{ mt: 2 }}>
            We&apos;ve sent a confirmation email to:
            <br />
            <strong>{form.email || oauthEmail}</strong>
            <br />
            <br />
            Click the link inside to verify your account and start exploring
            LumiQ. If you don&apos;t see the email, check your spam folder or
            click resend below.
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              width: '100%',
              gap: 1,
            }}>
            <Button
              variant="primary"
              label="Resend Email"
              fullWidth={false}
              sx={{ mt: 2, height: 48, fontSize: 16, width: '50%' }}
              onClick={async () => {
                AnalyticsService.buttonClick({
                  name: EVENT_CONSTANTS.BUTTON.RESEND,
                });
                await onSubmit();
                SnackbarUtils.success(
                  'Confirmation email resent successfully. Please check your inbox.',
                );
              }}
            />
            <Button
              variant="primary"
              label="Contact Support"
              fullWidth={false}
              sx={{ mt: 2, height: 48, fontSize: 16, width: '50%' }}
              onClick={() => {
                window.Intercom?.('show');
              }}
            />
          </Box>
        </>
      )}
    </ModalContentContainer>
  );
};

const styles = {
  benefitList: { display: 'flex', flexDirection: 'column', gap: 1.5 },
  benefitItem: { display: 'flex', alignItems: 'center', gap: 1.5 },
  checkboxIcon: {
    color: 'secondary.main',
    width: 24,
    height: 24,
    padding: 0.5,
    borderRadius: 3,
    backgroundColor: 'white',
  },
};
